import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useCategories } from '../hooks/useCategories'
import { photoDeCategorie } from '../data/visuels'
import { t } from '../utils/format'
import './Categories.css'

/**
 * Les rayons du magasin.
 *
 * Trois choses ont change, et aucune n'est cosmetique.
 *
 * 1. Plus de repli code en dur. Il tenait six categories avec six photos
 *    Unsplash : quand l'API ne repondait pas, le visiteur voyait six rayons
 *    illustres de photos qui ne sont pas les notres, et l'une d'elles
 *    (« Décoration ») n'existait meme pas dans la liste. Un repli qui
 *    invente son contenu ne protege pas d'une panne, il la cache.
 *
 * 2. Les categories qui ont des produits passent en tete. L'ordre de la base
 *    placait le seul rayon garni en quatrieme position : la premiere tuile
 *    de la page menait a un catalogue vide.
 *
 * 3. Un rayon vide ne se clique pas. Il s'annonce. Envoyer quelqu'un sur une
 *    page « aucun produit » est pire que de lui dire tout de suite que le
 *    rayon ouvre bientot.
 *
 * L'image vient de photoDeCategorie() : image_url de la base si
 * l'administrateur en a mis une, sinon la photo versionnee avec le site,
 * sinon rien. « Rien » donne une tuile typographique batie sur les couleurs
 * que la categorie porte deja en base. Aucune photo de banque nulle part.
 */

const TEXTES = {
  fr: {
    titre1: 'Tout ce dont vous avez besoin,',
    titre2: 'au même endroit',
    accroche:
      'Sept rayons, du mobilier de séjour à l\'équipement sportif. Nous ouvrons les stocks rayon par rayon.',
    produits: (n) => `${n} produit${n > 1 ? 's' : ''}`,
    voir: 'Voir les produits',
    bientot: 'Bientôt en ligne',
    toutVoir: 'Voir tout le catalogue',
    toutVoirNote: 'Les rayons déjà garnis, au complet',
  },
  en: {
    titre1: 'Everything you need,',
    titre2: 'all in one place',
    accroche:
      'Seven departments, from living room furniture to fitness equipment. We open each one as stock arrives.',
    produits: (n) => `${n} product${n > 1 ? 's' : ''}`,
    voir: 'View products',
    bientot: 'Opening soon',
    toutVoir: 'View the full catalog',
    toutVoirNote: 'Every department already stocked',
  },
}

/**
 * Les rayons garnis d'abord, puis l'ordre choisi dans l'administration.
 *
 * product_count peut valoir null quand la charge utile ne le porte pas : on
 * ne le traite pas comme un zero, faute de savoir. Un rayon dont on ignore
 * le contenu reste cliquable.
 */
function parGarnissage(categories) {
  const rang = (c) => (c.product_count === 0 ? 1 : 0)

  return [...categories].sort(
    (a, b) => rang(a) - rang(b) || (a.sort_order ?? 0) - (b.sort_order ?? 0),
  )
}

function Tuile({ categorie, lang, texte, large }) {
  const nom = t(categorie.name, lang)
  const photo = photoDeCategorie(categorie)
  const compte = categorie.product_count
  const vide = compte === 0

  const legende =
    compte === null || compte === undefined
      ? texte.voir
      : vide
        ? texte.bientot
        : texte.produits(compte)

  /*
   | Deux tuiles, et non une tuile avec ou sans image.
   |
   | La premiere version gardait le meme gabarit dans les deux cas : un
   | cadre d'image vide au-dessus d'un pied. Sept cadres vides a la suite se
   | lisaient comme sept images qui n'ont pas charge, ce qui est exactement
   | l'impression a eviter. Sans photo, la tuile devient donc un panneau
   | typographique : le nom du rayon porte la tuile, et le vide se lit comme
   | un choix.
   */
  const contenu = photo ? (
    <>
      <div className="cat-visuel">
        <img src={photo} alt={nom} className="cat-photo" loading="lazy" />
        <span className="cat-voile" aria-hidden="true" />
      </div>

      <div className="cat-pied">
        <div className="cat-texte">
          <h3 className="cat-nom">{nom}</h3>
          <p className={`cat-compte${vide ? ' cat-compte-vide' : ''}`}>{legende}</p>
        </div>
        {! vide && (
          <span className="cat-fleche" aria-hidden="true">
            <ArrowRight size={17} />
          </span>
        )}
      </div>
    </>
  ) : (
    <>
      <span className="cat-hachures" aria-hidden="true" />
      <div className="cat-panneau">
        <h3 className="cat-nom cat-nom-grand">{nom}</h3>
        <div className="cat-panneau-pied">
          <p className={`cat-compte${vide ? ' cat-compte-vide' : ''}`}>{legende}</p>
          {! vide && (
            <span className="cat-fleche" aria-hidden="true">
              <ArrowRight size={17} />
            </span>
          )}
        </div>
      </div>
    </>
  )

  const classes = [
    'cat-tuile',
    large ? 'cat-tuile-large' : '',
    photo ? 'cat-tuile-photo' : 'cat-tuile-nue',
    vide ? 'cat-tuile-vide' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const style = { '--cat-accent': categorie.accent, '--cat-teinte': categorie.color }

  if (vide) {
    return (
      <div className={classes} style={style}>
        {contenu}
      </div>
    )
  }

  return (
    <Link to={`/catalogue?category=${categorie.slug}`} className={classes} style={style}>
      {contenu}
    </Link>
  )
}

export default function Categories({ lang }) {
  const texte = TEXTES[lang] ?? TEXTES.fr
  const { categories, loading } = useCategories()
  const rangees = parGarnissage(categories)

  return (
    <section className="categories" id="categories">
      <div className="categories-inner">
        <header className="categories-entete reveal">
          <h2 className="categories-titre">
            {texte.titre1}
            <br />
            <span className="categories-titre-accent">{texte.titre2}</span>
          </h2>
          <p className="categories-accroche">{texte.accroche}</p>
        </header>

        <div className="categories-grille reveal">
          {loading && ! rangees.length
            ? Array.from({ length: 7 }).map((_, i) => (
                <div
                  key={i}
                  className={`cat-tuile cat-tuile-attente${i === 0 ? ' cat-tuile-large' : ''}`}
                  aria-hidden="true"
                />
              ))
            : rangees.map((categorie, i) => (
                <Tuile
                  key={categorie.slug ?? categorie.id}
                  categorie={categorie}
                  lang={lang}
                  texte={texte}
                  large={i === 0}
                />
              ))}

          {/*
            | La derniere cellule de la grille porte l'appel au catalogue
            | complet, au lieu de laisser un trou et de poser un bouton
            | centre sous la section.
          */}
          <Link to="/catalogue" className="cat-tuile cat-tuile-appel">
            <span className="cat-appel-titre">{texte.toutVoir}</span>
            <span className="cat-appel-note">{texte.toutVoirNote}</span>
            <span className="cat-appel-fleche">
              <ArrowRight size={18} />
            </span>
          </Link>
        </div>
      </div>
    </section>
  )
}
